import { Application, Component } from '@tencent/alloy-renderer';
import { EventArea } from '@tencent/alloy-editor';
import i18n from '@i18next/i18n';
import EmptyModel from '@src/slide/domain/ui-model/element-modeles/empty-model';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { reaction, IReactionDisposer } from 'mobx';
import {
    IEditorProps,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import {
    noteBodyStyle,
    placeholderBodyStyle,
    MAX_NOTE_CANVAS_HEIGHT,
    NOTE_CANVAS_HEIGHT_CHANGE_STEP,
    NOTE_CANVAS_HEIGHT_CHANGE_TRIGGER,
} from './note-data';
import {
    getPlaceholderParagraphList,
    styleRenderCallback,
    LINE_HEIGHT,
    ForceStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/note/common-note-data';

import { NOTE_DEFAULT_HEIGHT, NOTE_COLLAPSE_HEIGHT } from '@editor/src/js/constants';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import Task from '@src/slide/infrastructure/module/dependent/taskManager/task';
import isEqual from 'lodash/isEqual';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { getSlideIndex } from '@src/slide/domain/ui-model/element-modeles/property/slide-number/operation';
import { getQuery } from 'sutils';

import type { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IPlayManager } from '@src/slide/application/service/play-service/play-manager.interface';
import type { Result } from '@src/slide/application/service/search-replace-manager/search-replace-manager-v3.interface';
import {
    IUncommonFontLogic,
} from '@src/slide/content-viewer/service/font-manager/core/font-manager.interface';
import { NoteEditLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/edit-note/logic/note-edit-logic-service';
import { IEditorServices } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-service.interface';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';

export interface NoteAreaProps {
    application: Application<BrowserTarget>;
    canvasElement: HTMLCanvasElement,
    topBlankDiv?: HTMLDivElement | null,
    bottomBlankDiv?: HTMLDivElement | null,

    forceStyle?: ForceStyle;
    forceReadOnly?: boolean;

    /** 该备注实例是否被展示； */
    getDisplayStatus?: () => boolean;
    uncommonFontLogic?: IUncommonFontLogic;

    // 备注占位文字，不指定的话会显示为默认文字
    placeholderText?: string
}

const NOTE_DELAY_RENDER_KEY = 'note_delay_render_task';
export class NoteArea extends Component {
    public widthHasChanged: boolean;
    private readonly application: Application<BrowserTarget>;
    private forceStyle?: ForceStyle;
    private forceReadOnly = false;
    private reactionHandler: any;
    private textEditor: TextEditor | null;
    private model: any;
    private slide: any;
    private isFocus: boolean;
    private placeHolderComponent: TextEditor | null;
    private cacheSelectionRect: {
        top: number,
        bottom: number,
    };
    private reactionSlideLengthHandler: any;
    private reactionSelectedElementsHandler: IReactionDisposer;
    private containerHeight: number;
    private containerWidth: number;
    private editStateReaction: IReactionDisposer;
    private playingStateReaction: IReactionDisposer;
    private taskId: number;
    private offsetTop: number;
    private cachedScrollTop: number;
    private scrollPending: boolean;
    private canvasElement: HTMLCanvasElement;
    private topBlankDiv?: HTMLDivElement | null;
    private bottomBlankDiv?: HTMLDivElement | null;
    private isLargeNote: boolean;
    private topBlankDivHeight: number;
    private bottomBlankDivHeight: number;
    private noteAreaInstanceId: number;
    private getDisplayStatus: () => boolean;
    private uncommonFontLogic: IUncommonFontLogic | undefined;
    private placeholderText?: string;

    public constructor(noteProps: NoteAreaProps) {
        super();
        this.noteAreaInstanceId = this.generateInstanceId();
        const {
            application,
            forceStyle,
            canvasElement,
            topBlankDiv,
            bottomBlankDiv,
            forceReadOnly,
            getDisplayStatus = () => true,
        } = noteProps;
        this.uncommonFontLogic = noteProps.uncommonFontLogic;
        this.placeholderText = noteProps.placeholderText;
        this.application = application;
        this.canvasElement = canvasElement;
        this.topBlankDiv = topBlankDiv;
        this.bottomBlankDiv = bottomBlankDiv;
        this.forceStyle = forceStyle;
        this.forceReadOnly = forceReadOnly ?? false;
        this.getDisplayStatus = getDisplayStatus;
        this.isFocus = false;
        this.containerHeight = NOTE_DEFAULT_HEIGHT;
        this.containerWidth = application.width || 0;
        this.offsetTop = 0;
        this.cachedScrollTop = 0;
        this.updateArea();
        this.addObserver();
        this.addSlideObserver();
        this.cacheSelectionRect = {
            top: 0,
            bottom: 0,
        };
        this.taskId = 0;
        this.topBlankDivHeight = 0;
        this.bottomBlankDivHeight = 0;
    }

    public dispose(): void {
        super.dispose();
        this.removeSlideObserver();
        this.removeObserver();
        this.resetBlankDiv();
    }

    public updateEditorProps(): void {
        // editorProps在renderNote内生成，所以需要执行此函数
        this.renderNote();
    }

    public updateEditorPropsWithoutValueChange(): void {
        const props = this.getEditorProps(false);
        this.textEditor?.updateEditorProps(props);
    }

    public triggerBlur(): void {
        if (this.isFocus) {
            this.model.noteInstance?.textEditor?.triggerBlur();
            this.isFocus = false;
        }
    }

    public saveContainerHeight(height: number): void {
        this.containerHeight = height || 0;
    }

    public saveContainerWidth(width: number): void {
        this.containerWidth = width || 0;
    }

    // 更新备注高度，备注的 canvas 实例高度以容器高度和文本高度中最大的为准，但不超过 MAX_NOTE_CANVAS_HEIGHT
    public updateNoteHeight = (height?: number): void => {
        if (!this.textEditor) {
            this.renderPlaceholder();
            return;
        }
        const textRectHeight = height || this.getTextRectHeight();
        const applicationHeight = this.application.view.height;
        const expectHeight = this.isScrollMode ? Math.min(textRectHeight, MAX_NOTE_CANVAS_HEIGHT) : textRectHeight;
        if (!this.shouldUpdateNoteHeight(expectHeight, applicationHeight, this.containerHeight)) {
            return;
        }
        if (expectHeight > applicationHeight || this.containerHeight > applicationHeight) {
            // 期望高度大于 canvas 高度 或者 容器高度大于 canvas 高度， 即需要增大 canvas 高度
            this.setApplicationHeight(Math.max(this.containerHeight, expectHeight));
        } else if (expectHeight < applicationHeight && this.containerHeight < applicationHeight) {
            // 期望高度小于 canvas 高度 且 canvas 高度大于 容器高度， 则需要减小 canvas 高度
            this.setApplicationHeight(Math.max(this.containerHeight, expectHeight));
        }
        this.updateBlankDivHeight(textRectHeight, this.application.height);
        this.updateEditorPropsWithoutValueChange();
    };

    public scrollContainerIfHeightChange = (textRectHeight: number): void => {
        if (!this.textEditor) {
            return;
        }
        const applicationHeight = this.application.view.height;
        if (textRectHeight <= this.containerHeight && applicationHeight <= this.containerHeight) {
            return;
        }
        if (textRectHeight === this.containerHeight + this.topBlankDivHeight + this.bottomBlankDivHeight) {
            return;
        }
        this.updateContainerScroll();
    };

    public updateForceStyle(forceStyle?: ForceStyle): void {
        if (isEqual(this.forceStyle, forceStyle)) return;

        this.forceStyle = forceStyle;

        if (this.showPlaceHolder()) {
            // 1. 触发一次强制渲染, 以应用 `styleRenderCallback` 样式
            this.placeHolderComponent?.editorContainer?.forceRender();
        } else {
            // 1. 触发一次强制渲染, 以应用 `styleRenderCallback` 样式
            this.textEditor?.editorContainer.forceRender();
            // 2. style 变化可能影响内容高度, 需要更新备注高度
            this.updateNoteHeight();
        }
    }

    public resetScrollState = () => {
        this.offsetTop = 0;
        this.resetBlankDiv();
        (this.getContainerElem() as HTMLDivElement).scrollTop = 0;
    };

    private shouldUpdateNoteHeight(expectHeight: number, applicationHeight: number, containerHeight: number) {
        if (this.widthHasChanged) {
            this.widthHasChanged = false;
            return true;
        }
        // 期望高度与当前备注高度相同，且期望备注高度大于备注容器高度
        if (expectHeight === applicationHeight && containerHeight < expectHeight) {
            return false;
        }
        // 期望高度小于当前备注高度，且当前备注高度与容器高度相同
        if (expectHeight < applicationHeight && containerHeight === applicationHeight) {
            return false;
        }
        return true;
    }

    private addSlideObserver() {
        this.reactionHandler = reaction(
            () => this.getAppServices().viewableServiceLayer.selectionService.currentSlide,
            () => {
                this.triggerBlur();
                this.removeObserver();
                this.resetBlankDiv();
                this.updateArea(false);
                this.addObserver();
                this.updateEditorInstance();
                this.resetContainerScroll();
            },
        );
        this.reactionSlideLengthHandler = reaction(
            () => this.getAppServices().viewableServiceLayer.selectionService.slides.length,
            () => {
                if (this.slide?.key === this.getAppServices().viewableServiceLayer.selectionService.currentSlide?.key) {
                    this.updateArea();
                }
            },
        );
    }

    private removeSlideObserver() {
        this.reactionHandler();
        this.reactionSlideLengthHandler();
    }

    private addObserver() {
        this.slide?.changeSetEngine.addObserveForChangeAtPath('noteSlideId', this.updateArea);
        if (this.model.noteTextBody?.changeSetEngine) {
            this.model.noteTextBody.changeSetEngine.addChangesetObserver(this.updateArea);
        }
        if (this.slide) {
            // 权限变更监听
            this.editStateReaction = reaction(() => ({
                slideEditState: this.slide?.pptEditorState,
            }), () => this.updateArea());
        }

        // 监听编辑、播放状态的切换；比如当用户从本地播放退出到编辑态时，尝试更新
        this.playingStateReaction = reaction(
            () => this.getPlayManager().isPlaying,
            () => this.updateArea(),
        );

        const ContainerElem = this.getContainerElem();
        if (ContainerElem && this.isScrollMode) {
            ContainerElem.addEventListener('scroll', this.handleNoteScroll);
        }

        this.reactionSelectedElementsHandler = reaction(
            () => this.getAppServices().viewableServiceLayer.selectionService.selectedElements.length,
            (length) => {
                if (length !== 0) {
                    // 此时选中了其他元素，如果备注区域在编辑态就需要失焦
                    this.triggerBlur();
                }
            },
        );

        this.uncommonFontLogic?.addObserverForUncommonFontLoaded(this.onUncommonFontLoaded);
    }

    private removeObserver() {
        if (this.slide?.changeSetEngine) {
            this.slide.changeSetEngine.removeObserveForChangeAtPath('noteSlideId', this.updateArea);
        }
        if (this.model.noteTextBody?.changeSetEngine) {
            this.model.noteTextBody.changeSetEngine.removeChangesetObserver(this.updateArea);
        }
        this.editStateReaction?.();
        this.reactionSelectedElementsHandler?.();
        this.playingStateReaction?.();
        this.uncommonFontLogic?.removeObserverForUncommonFontLoaded(this.onUncommonFontLoaded);
    }

    private updateArea = (updateImmediately = true) =>  {
        this.model = this.getAppServices().viewableServiceLayer.selectionService.currentSlide?.noteData
            || new EmptyModel({});
        this.slide = this.getAppServices().viewableServiceLayer.selectionService.currentSlide;
        updateImmediately === false ? this.delayRenderNote() : this.renderNote();
    };

    private renderNote = () => {
        if (!this.getDisplayStatus()) return;

        if (this.showPlaceHolder()) {
            // 占位符
            this.renderPlaceholder();
        } else {
            // 文本框
            this.renderTextEditor();
        }
    };

    private delayRenderNote = () => {
        if (!this.getDisplayStatus()) return;

        // 备注渲染任务只保留最新的一个
        if (TaskManager.getTaskWithKey(NOTE_DELAY_RENDER_KEY + this.noteAreaInstanceId + this.taskId)) {
            TaskManager.cancelTaskByKey(NOTE_DELAY_RENDER_KEY + this.noteAreaInstanceId + this.taskId);
        }
        this.taskId = this.taskId + 1;
        const task = new Task(NOTE_DELAY_RENDER_KEY + this.noteAreaInstanceId + this.taskId, TaskPriority.LOW, () => {
            this.renderNote();
            // 查找替换切页时会给查找到的对象加mark，异步加载的备注需要再加一次
            this.addMarkIfInSearch();
        });
        // 移除切页前的备注，等异步加载新的数据
        this.removeLastEditor();
        setTimeout(() => {
            TaskManager.addTask(task);
        });
    };


    private removeLastEditor = () => {
        if (this.textEditor) {
            // 移除现有的备注
            this.textEditor.dispose();
            this.removeChild(this.textEditor.editorContainer);
            this.textEditor = null;
        }
        if (!this.showPlaceHolder() && this.placeHolderComponent) {
            // 如果切页后备注有内容，则把现有的 placeHolder 也移除
            this.placeHolderComponent.dispose();
            this.removeChild(this.placeHolderComponent.editorContainer);
            this.placeHolderComponent = null;
        }
    };

    private isEditable = () => {
        if (Number(getQuery('editorState')) > PPTEditorStateType.EDITABLE) {
            return false;
        }

        return !this.forceReadOnly && this.model?.currentEditState === PPTEditorStateType.EDITABLE;
    };
    private slideExistence = () => this.getAppServices().viewableServiceLayer.selectionService.currentSlideIndex !== -1;
    private isNoteHide = () => this.containerHeight <= NOTE_COLLAPSE_HEIGHT;

    private showPlaceHolder = () => {
        if (!this.slideExistence()) return true;
        if (this.isFocus || !this.isEditable()) return false;
        return isEditorEmpty(this.model.noteTextBody?.paragraphList || []);
    };

    private updateEditorInstance = () => {
        this.model.noteTextBody?.setEditorComponent(this.textEditor);
    };

    /*
     * 获取note中命中的搜索结果
     */
    private getNoteSearchResult = (): Result | undefined => {
        const searchReplaceManager = this.getSearchreplaceManager();

        if (searchReplaceManager) {
            const { sdkData } = this.model;
            const { currentPageResult, isSearchingFlag } = searchReplaceManager;

            if (sdkData && currentPageResult && isSearchingFlag) {
                return searchReplaceManager.currentPageResult
                    ?.find(searchResult => searchResult.component === sdkData.note);
            }
        }

        return undefined;
    };

    private renderTextEditor = () => {
        if (this.placeHolderComponent) {
            this.placeHolderComponent.dispose();
            this.removeChild(this.placeHolderComponent.editorContainer);
            this.placeHolderComponent = null;
        }
        // 在获取Props之前更新application size
        this.updateApplicationSize(this.containerWidth);
        const editorProps: IEditorProps = this.getEditorProps(true);
        if (this.textEditor) {
            this.textEditor.updateEditorProps(editorProps);
        } else {
            this.textEditor = new TextEditor(editorProps, this.getTextServices());
            this.addChild(this.textEditor.editorContainer);
        }
        if (this.model.noteTextBody?.editorComponent !== this.textEditor) {
            this.model.noteTextBody?.setEditorComponent(this.textEditor);
        }
        this.model.noteInstance = this;
        this.updateNoteHeight();  // textEditor的高度可能大于容器高度，需要更新一下
    };

    private getEditorProps = (shouldUpdate): IEditorProps => ({
        shouldUpdate,
        paragraphList: this.model.noteTextBody?.paragraphList,
        textBodyStyle: this.model.noteTextBody?.textBodyStyle,
        modelKey: this.model.key,
        textRect: this.getTextRect(),
        getSelected: () => this.model.selected,
        editable: this.isEditable(),
        selectable: true,
        eventArea: EventArea.maxBlock,
        slideNum: getSlideIndex(this.model?.slideRef) + 1,
        datetime: this.getAppServices().getInfrastructureService().initDatetime.toString(),
        needRenderTextExtra: false,
        onEnterEditing: this.handleEnterEditing,
        onTextChange: this.handleTextChange,
        onExitEditing: this.handleExitEditing,
        onChange: this.handleChange,
        onBeforeValueChange: this.beforeEditorValueChange,
        onStyleChange: this.handleEditorStyleChange,
        handleSelectionChange: this.handleSelectionChange,
        resetShouldUpdate: () => {
            this.model.shouldUpdate = false;
        },
        cursor: this.isEditable() ? 'text' : 'point',
        onClick: this.tipsIfLock,
        styleRenderCallback: this.handleStyleRenderCallback,
        getApplication: () => this.application,
        isNotFillInvisibleText: true,
    });

    private getPlaceholderProps = (shouldUpdate = true): IEditorProps => {
        const placeholderText = (this.placeholderText || (this.slideExistence() ? i18n.t('点击添加备注') : i18n.t('请先添加页面再添加备注'))) as string;
        return {
            shouldUpdate,
            paragraphList: getPlaceholderParagraphList(placeholderText) as [],
            modelKey: this.model?.key,
            textBodyStyle: { bodyStyle: placeholderBodyStyle },
            textRect: this.getTextRect(),
            selected: false,
            editable: false,
            eventArea: EventArea.maxBlock,
            slideNum: getSlideIndex(this.model?.slideRef) + 1,
            cursor: 'point',
            onClick: this.handlePlaceholderClick,
            getApplication: () => this.application,
            styleRenderCallback: this.handleStyleRenderCallbackForPlaceholder,
        };
    };

    private getTextRect = () => {
        const { height, width } = this.application;
        return {
            height,
            width,
            left: 0,
            top: 0 - this.offsetTop,
        };
    };

    private handleChange = (...rest) => {
        this.updateData(...rest);
    };

    private beforeEditorValueChange = () => {
        const textSelectionUndoRedo = this.getTextSelectionUndoRedo();
        // 文字有值变更，清空暂存的选区 redo 栈
        textSelectionUndoRedo?.clearRedo();
        // 暂存 editor 数据变更前的文字选区
        textSelectionUndoRedo?.stashSelectionBefore(this.textEditor!.getSelection(), this.model.parent.id);
    };

    private updateData = ({ paragraphList, textBodyStyle } = { paragraphList: [], textBodyStyle: {} }) => {
        this.model?.updateParagraphList(paragraphList, textBodyStyle);

        // 备注视图子窗口: 提交变更到sdk的同时, 将变更同步到主窗口再做一次提交。
        // a. 子窗口是 “离线” 的 (禁用 socket)，updateParagraphList 只为了更新本地数据, 不会产生网络请求, 也不会写离线
        // b. 同步主窗口后, 主窗口会重新调用 updateParagraphList
        if (ENV.IS_SUBSIDIARY) {
            window.dispatchEvent(new CustomEvent('SUBSIDIARY_PLAYER_UPDATE_NOTE', {
                detail: {
                    slideId: this.model?.slideRef?.id,
                    paragraphList,
                    textBodyStyle,
                },
            }));
        }
    };

    // textChange时如果内容高度超过了备注现有高度就需要更新
    private handleTextChange = (rect) => {
        // 触发锁页
        this.getAppServices().viewableServiceLayer.textEditorStateService.editorContentChangeFlag += 1;
        const height = this.getTextRectHeight(rect);
        this.updateNoteHeight(height);
        this.scrollContainerIfHeightChange(height);
    };

    private handleStyleRenderCallback = (style, type) => {
        const forceStyle: ForceStyle = {
            bodyStyle: noteBodyStyle,
            ...this.forceStyle,
        };
        const noteSearchResult = this.getNoteSearchResult();

        if (noteSearchResult) {
            // 添加搜索高亮时设置了 isSearchResult，以此判断备注中的查找结果，保留其背景色
            if (
                type === 'textRun'
                && style.isSearchResult
            ) {
                forceStyle.textRunStyle = {
                    background: style.background,
                };
            }
        }

        return styleRenderCallback(forceStyle)(style, type);
    };

    private handleStyleRenderCallbackForPlaceholder = (style, type) => {
        // placeholder 只有文字，只处理 `textRun`
        if (type === 'textRun') {
            return {
                ...style,
                ...this.forceStyle?.textRunStyle,
            };
        }

        return style;
    };

    private handleSelectionChange = () => {
        const textSelectionUndoRedo = this.getTextSelectionUndoRedo();
        // 暂存当前的文字选区，用于选区的 undo redo
        // eslint-disable-next-line max-len
        this.textEditor && textSelectionUndoRedo?.stashSelectionAfter(this.textEditor.getSelection(), this.model.parent.id);
        this.updateContainerScroll();
    };

    /**
     * 滚动的计算内，所有的位置以canvas左上角为原点，由此解释以下变量
     * wrapperTop: 容器元素的scrollTop
     * wrapperBottom: 容器元素的高度+scrollTop
     * top: 选区顶部相对于canvas左上角的 y 值
     * bottom: 选区底部相对于canvas左上角的 y 值
     * cacheSelectionRect: 缓存上一次的选区
     */
    private updateContainerScroll = () => {
        const selectionRect = this.textEditor?.getExpandedRect();
        const noteWrapperElem = this.getContainerElem();
        if (selectionRect && noteWrapperElem) {
            const top = Math.ceil(selectionRect.top);
            const bottom = Math.ceil(selectionRect.bottom);
            const oldTop = this.cacheSelectionRect.top;
            const oldBottom = this.cacheSelectionRect.bottom;
            const wrapperTop = noteWrapperElem.scrollTop - this.offsetTop;
            const wrapperBottom = noteWrapperElem.clientHeight + noteWrapperElem.scrollTop - this.offsetTop;
            if (bottom > Math.max(oldBottom, wrapperBottom)) {
                // 向下滚动至底部选区
                noteWrapperElem.scrollTo(0, bottom - noteWrapperElem.clientHeight + this.offsetTop);
            } else if (top < Math.min(oldTop, wrapperTop)) {
                // 向上滚动至顶部选区
                if (bottom <= oldBottom) {  // 避免 ctrl+a 全选时滚动到顶部
                    noteWrapperElem.scrollTo(0, top + this.offsetTop);
                }
            } else if (bottom < Math.min(oldBottom, wrapperTop + LINE_HEIGHT)) {
                // 向上回滚至底部选区露出一行
                noteWrapperElem.scrollTo(0, bottom - LINE_HEIGHT + this.offsetTop);
            } else if (top > Math.max(oldTop, wrapperBottom - LINE_HEIGHT)) {
                // 向下回滚至顶部选区露出一行
                noteWrapperElem.scrollTo(0, top - noteWrapperElem.clientHeight + LINE_HEIGHT + this.offsetTop);
            }
            this.cacheSelectionRect = { top, bottom };
        }
    };

    private resetContainerScroll = () => {
        const noteWrapperElem = this.getContainerElem();
        if (noteWrapperElem?.scrollTop) {
            noteWrapperElem.scrollTo(0, 0);
        }
    };

    private getContainerElem = () => {
        if (this.isScrollMode) {
            // 备注的模拟滚动模式在父元素上增加了一层div
            return this.canvasElement?.parentElement?.parentElement;
        }
        return this.canvasElement?.parentElement;
    };

    private handleEditorStyleChange = (style) => {
        if (this.model.noteTextBody) {
            this.model.noteTextBody.selectionStyles = style.style;
        }
    };

    private handleEnterEditing = () => {
        this.isFocus = true;
    };

    private handlePlaceholderClick = () => {
        this.tipsIfLock();
        if (this.getUserPriviledgeService().pptEditorState !== PPTEditorStateType.EDITABLE || !this.model) return;
        this.isFocus = true;
        this.renderNote();
        requestAnimationFrame(() => {
            this.model.noteTextBody?.editorInstance.setFocus();
        });
    };

    private renderPlaceholder() {
        if (this.textEditor) {
            this.textEditor.dispose();
            this.removeChild(this.textEditor.editorContainer);
            this.textEditor = null;
        }
        // 在获取Props之前更新application size
        this.updateApplicationSize(this.containerWidth, this.containerHeight);
        const editorProps: IEditorProps = this.getPlaceholderProps();
        if (this.placeHolderComponent) {
            this.placeHolderComponent.updateEditorProps(editorProps);
        } else if (!this.isNoteHide()) {
            this.placeHolderComponent = new TextEditor(editorProps, this.getTextServices());
            this.addChild(this.placeHolderComponent.editorContainer);
        }
    }

    private handleExitEditing = () => {
        if (this.isFocus) {
            this.isFocus = false;
            if (this.showPlaceHolder()) {
                this.renderNote();
            }
        }
    };

    private tipsIfLock = () => {
        if (this.getUserPriviledgeService().pptEditorState > PPTEditorStateType.EDITABLE) {
            this.getAppServices().viewableServiceLayer.tipsManager.showCannotEditTips();
        }
    };

    private addMarkIfInSearch = () => {
        const searchReplaceManager = this.getSearchreplaceManager();
        const noteSearchResult = this.getNoteSearchResult();
        if (noteSearchResult && searchReplaceManager) {
            this.model.noteInstance?.textEditor?.addSearchMark(noteSearchResult.result, searchReplaceManager.keyword);
        }
    };

    private updateCanvasRenderOffsetScrollDown = (containerElem: HTMLDivElement, textHeight: number): void => {
        const { scrollTop, clientHeight } = containerElem;
        // canvas区域已经渲染的，底部剩余的不可见高度
        const canvasBottomDist = MAX_NOTE_CANVAS_HEIGHT -  (scrollTop - this.offsetTop + clientHeight);
        if (canvasBottomDist > NOTE_CANVAS_HEIGHT_CHANGE_TRIGGER) {
            return;
        }
        // 文本在canvas之外还没渲染的，底部剩余的高度
        const textBottomDist = textHeight - this.offsetTop - MAX_NOTE_CANVAS_HEIGHT;
        if (textBottomDist === 0) {
            return;
        }
        // canvas向下移动 200px 或者到文本底部
        this.offsetTop = Math.min(scrollTop - NOTE_CANVAS_HEIGHT_CHANGE_STEP, textHeight - MAX_NOTE_CANVAS_HEIGHT);
        this.scrollPending = true;
        this.textEditor?.updateEditorProps(this.getEditorProps(true));
        requestAnimationFrame(() => {
            this.scrollPending = false;
            this.topBlankDivHeight = this.offsetTop;
            this.bottomBlankDivHeight = Math.ceil(textHeight - this.offsetTop - MAX_NOTE_CANVAS_HEIGHT);
            this.setBlankDivHeight(this.topBlankDivHeight, this.bottomBlankDivHeight);
            (this.getContainerElem() as HTMLDivElement).scrollTop = scrollTop;
            if (this.cacheSelectionRect.top) {
                const newSelectionRect = this.textEditor?.getExpandedRect();
                const top = Math.ceil(newSelectionRect?.top || 0);
                const bottom = Math.ceil(newSelectionRect?.bottom || 0);
                this.cacheSelectionRect = { top, bottom };
            }
        });
    };

    private updateCanvasRenderOffsetScrollUp = (containerElem: HTMLDivElement, textHeight: number): void => {
        const { scrollTop } = containerElem;
        if (this.offsetTop === 0) {
            return;
        }
        // canvas区域已经渲染的，顶部剩余的不可见高度
        const canvasTopDist = scrollTop - this.offsetTop;
        if (canvasTopDist > NOTE_CANVAS_HEIGHT_CHANGE_TRIGGER) {
            return;
        }
        // canvas向上移动 200px 或者到文本顶部
        this.offsetTop = Math.max(scrollTop - NOTE_CANVAS_HEIGHT_CHANGE_STEP, 0);
        this.scrollPending = true;
        this.textEditor?.updateEditorProps(this.getEditorProps(true));
        requestAnimationFrame(() => {
            this.scrollPending = false;
            this.topBlankDivHeight = this.offsetTop;
            this.bottomBlankDivHeight = textHeight - this.offsetTop - MAX_NOTE_CANVAS_HEIGHT;
            this.setBlankDivHeight(this.topBlankDivHeight, this.bottomBlankDivHeight);
            (this.getContainerElem() as HTMLDivElement).scrollTop = scrollTop;
            if (this.cacheSelectionRect.top) {
                const newSelectionRect = this.textEditor?.getExpandedRect();
                const top = Math.ceil(newSelectionRect?.top || 0);
                const bottom = Math.ceil(newSelectionRect?.bottom || 0);
                this.cacheSelectionRect = { top, bottom };
            }
        });
    };

    private handleNoteScroll = (): void => {
        if (this.scrollPending) {
            return;
        }
        if (!this.isLargeNote || !this.isScrollMode) {
            return;
        }
        const containerElem = this.getContainerElem() as HTMLDivElement;
        const { scrollTop } = containerElem;
        const isScrollDown = this.cachedScrollTop < scrollTop;
        const textHeight = this.textEditor?.getTextAreaHeightSync() || 0;
        if (isScrollDown) {
            // 向下滚动
            this.updateCanvasRenderOffsetScrollDown(containerElem, textHeight);
        } else {
            // 向上滚动
            this.updateCanvasRenderOffsetScrollUp(containerElem, textHeight);
        }
        this.cachedScrollTop = scrollTop;
    };

    private setBlankDivHeight = (topHeight, bottomHeight): void => {
        if (!this.topBlankDiv || !this.bottomBlankDiv) {
            return;
        }
        if (this.topBlankDiv.style.height === topHeight && this.bottomBlankDiv.style.height === bottomHeight) {
            return;
        }
        this.topBlankDiv.style.height = `${topHeight || this.topBlankDivHeight}px`;
        this.bottomBlankDiv.style.height = `${bottomHeight || this.bottomBlankDivHeight}px`;
    };

    private getTextRectHeight = (textRect?: { height: number, width: number}): number => {
        // 避免小数影响计算，取到数值后进行取整
        const height = Math.ceil(textRect?.height || this.textEditor?.getTextAreaHeightSync() || 0);
        return height;
    };

    private updateBlankDivHeight = (textRectHeight, applicationHeight): void => {
        if (!this.isScrollMode) {
            return;
        }
        if (textRectHeight <= MAX_NOTE_CANVAS_HEIGHT) {
            if (this.isLargeNote) {
                this.isLargeNote = false;
                this.offsetTop = 0;
                this.resetBlankDiv();
            }
            return;
        }
        this.isLargeNote = true;
        const scrollTop = this.getContainerElem()?.scrollTop || 0;
        const topDivHeight = scrollTop;
        const bottomDivHeight = Math.max(textRectHeight - applicationHeight - scrollTop, 0);
        this.setBlankDivHeight(topDivHeight, bottomDivHeight);
    };

    private resetBlankDiv = () => {
        this.topBlankDivHeight = 0;
        this.bottomBlankDivHeight = 0;
        this.setBlankDivHeight(0, 0);
    };

    private updateApplicationSize = (width: number, height?: number) => {
        this.setApplicationWidth(width);
        this.setApplicationHeight(height);
    };

    private get isScrollMode(): boolean {
        return !!(this.topBlankDiv && this.bottomBlankDiv);
    }

    public get isEditing(): boolean {
        return !!this.textEditor?.isEditing;
    }

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }

    private getUserPriviledgeService(): IUserPriviledgeService {
        return this.getAppServices().viewableServiceLayer.userPriviledgeService;
    }

    private getPlayManager(): IPlayManager {
        return this.getAppServices().viewableServiceLayer.playManager;
    }

    private generateInstanceId(): number {
        return Math.floor(Math.random() * 100000);
    }

    private getSearchreplaceManager() {
        return this.getAppServices().editableServiceLayer?.searchReplaceManager?.dangerousInstance;
    }
    private setApplicationHeight(height) {
        const relHeight = Math.ceil(height);
        if (height && this.application.height !== relHeight) {
            this.application.height = height;
        }
    }
    private setApplicationWidth(width) {
        const relWidth = Math.ceil(width);
        if (width && this.application.width !== relWidth) {
            this.application.width = width;
        }
    }

    private onUncommonFontLoaded = (uncommonFont: FontFace, fontName: string) => {
        this.textEditor?.onFontDownloadedCallback(fontName);
        this.uncommonFontLogic?.removeObserverForUncommonFontLoaded(this.onUncommonFontLoaded);
    };

    private getTextServices(): IEditorServices {
        return  {
            uiDataService: this.getAppServices().getUiDataService(),
            editingStateManager: this.getAppServices().viewableServiceLayer.editingStateManager,
            selectionService: this.getAppServices().viewableServiceLayer.selectionService,
            fontManager: this.getAppServices().viewableServiceLayer.fontManager,
            textEditorStateService: this.getAppServices().viewableServiceLayer.textEditorStateService,
            accessibilityManager: this.getAppServices().viewableServiceLayer.accessibilityManager,
            undoRedoService: this.getAppServices().editableServiceLayer?.undoRedoService,
            linkManager: this.getAppServices().viewableServiceLayer.linkManager,
            logicService: new NoteEditLogicService(),
        };
    }

    private getTextSelectionUndoRedo() {
        return this.getAppServices().editableServiceLayer?.textSelectionUndoRedo;
    }
}
